import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/operator-summary`.
 *
 * A single response feeding every widget on the admin operator
 * dashboard: four KPI tiles, the network ring, the 24h revenue
 * sparkline + current hourly rate, and the active-sessions table.
 * Generated server-side from MongoDB aggregations; kept here in
 * @evi/shared so the API and admin share one source of truth.
 */
// ── Active sessions table row ────────────────────────────────────────────
//
// The UI-level `ActiveSessionRow` type lives in @evi/ui next to the
// table component (it uses a narrower `status` union with capitalised
// operator-view values). This shared schema mirrors that shape so the
// API can emit rows the table consumes directly.
export const ActiveSessionRowStatusSchema = z.enum([
    'Charging',
    'Finishing',
    'Idle',
    'Faulted',
]);
export const ActiveSessionRowSchema = z.object({
    id: z.string(),
    stationName: z.string(),
    stationCode: z.string(),
    driverName: z.string(),
    energyKwh: z.number().nonnegative(),
    /** 0–100 progress toward a nominal full charge. */
    progressPct: z.number().min(0).max(100),
    status: ActiveSessionRowStatusSchema,
});
// ── Dashboard payload ────────────────────────────────────────────────────
const DeltaTileSchema = z.object({
    value: z.number(),
    deltaPct: z.number(),
});
export const OperatorDashboardDataSchema = z.object({
    revenueToday: DeltaTileSchema,
    /** Today's revenue per currency, cents of each (Warren, 29 Oct 2026: never USD and CAD summed at par).
     *  revenueToday.value stays the single nominal figure for the delta. Optional for API instances that
     *  predate it; empty when financials are hidden. */
    revenueTodayByCurrency: z.array(z.object({ currency: z.string(), total_cents: z.number() })).optional(),
    activeSessions: z.object({
        value: z.number().int().nonnegative(),
        ofTotal: z.number().int().nonnegative(),
        deltaCount: z.number().int(),
    }),
    uptime24h: DeltaTileSchema,
    energyMtdKwh: DeltaTileSchema,
    network: z.object({
        online: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        faulted: z.number().int().nonnegative(),
    }),
    /** Revenue series in USD, oldest first. Bucket cadence depends
     *  on `window`:
     *    '24h'    → 24 hourly buckets
     *    '7d'     → 7 daily buckets
     *    '30d'    → 30 daily buckets
     *    '90d'    → 13 weekly buckets
     *    'custom' → daily buckets up to 31 days, weekly beyond
     *  Max 53 = ceil(365 / 7), the weekly bucket count of the largest
     *  custom range the API's 365-day cap permits.
     *  Field name is `revenueLast24h` for backward-compat (rename
     *  to `revenueSeries` was considered but rejected — the spec
     *  said "keep if rename touches > 3 files" and there were 4
     *  references across api / shared / two admin files). */
    revenueLast24h: z.array(z.number().nonnegative()).min(1).max(53),
    /** Energy series in kWh, oldest first. Same bucket cadence and
     *  cardinality as `revenueLast24h`. Optional only for back-compat
     *  with serializations from API instances predating Reports
     *  Phase 2C — server-side always populates the field now. */
    energyLast24h: z.array(z.number().nonnegative()).min(1).max(53).optional(),
    /** Window the revenue series + summary aggregates cover.
     *  Echoes the query-string param the client supplied; lets the
     *  client render the active period chip without having to track
     *  what it asked for separately. 'custom' is never a valid
     *  ?period= value — it appears here when the request carried
     *  ?from=&to= instead of a preset period. */
    window: z.enum(['24h', '7d', '30d', '90d', 'custom']),
    /** Revenue in the trailing 60 minutes, in USD. Period-INVARIANT:
     *  this is a moment-in-time pricing read (what we'd bill per hour
     *  right now), not a window aggregate. Stays last-60m regardless
     *  of the `window` param. */
    currentRateUsdHr: z.number().nonnegative(),
    activeSessionsList: z.array(ActiveSessionRowSchema),
    totalActive: z.number().int().nonnegative(),
    generatedAt: z.string(),
});
/** Period selector for the dashboard's revenue series. Used by
 *  the API query string + the React hook + the chart's chip row.
 *  '90d' renders as 13 weekly buckets. Arbitrary custom ranges do
 *  NOT travel through this enum — they ride ?from=&to= and surface
 *  in the payload as window:'custom'. */
export const OperatorDashboardPeriodSchema = z.enum(['24h', '7d', '30d', '90d']);
//# sourceMappingURL=operator-dashboard.js.map